import { Box, Stack, SvgIcon, Typography } from "@mui/joy";
import { useRecoilState, useRecoilValue } from "recoil";
import { userWebChatAtom } from "recoil/userWebChatAtom";
import { theme } from "styles/theme";
import {
  pcSideTabAtom,
  PCSideTabType,
  isMyPageSectionPath,
  pcTabFromPath,
} from "recoil/pcSideTabAtom";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const HomeIcon = ({ focused }: { focused: boolean }) => (
  <SvgIcon
    width="31"
    height="30"
    viewBox="0 0 31 30"
    opacity={focused ? 1 : 0.3}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.75122 10.7991C5.80236 11.5582 5.25 12.7074 5.25 13.9226V23.0001C5.25 24.1046 6.14543 25.0001 7.25 25.0001H11.25C11.8023 25.0001 12.25 24.5523 12.25 24.0001V19.0001C12.25 17.8955 13.1454 17.0001 14.25 17.0001H16.25C17.3546 17.0001 18.25 17.8955 18.25 19.0001V24.0001C18.25 24.5523 18.6977 25.0001 19.25 25.0001H23.25C24.3546 25.0001 25.25 24.1046 25.25 23.0001V13.9226C25.25 12.7074 24.6976 11.5582 23.7488 10.7991L17.7488 5.99908C16.2879 4.83038 14.2121 4.83038 12.7512 5.99908L6.75122 10.7991Z"
      fill="#20272B"
    />
  </SvgIcon>
);

const ChatIcon = ({ focused }: { focused: boolean }) => (
  <SvgIcon width="31" height="30" viewBox="0 0 31 30" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.75 9C5.75 6.79086 7.54086 5 9.75 5H21.75C23.9591 5 25.75 6.79086 25.75 9V23.9196C25.75 24.7581 24.7801 25.2242 24.1253 24.7004L21.8457 22.8767C21.1364 22.3093 20.2552 22.0002 19.3469 22.0002H9.75C7.54086 22.0002 5.75 20.2093 5.75 18.0002V9ZM9.75 15.5002C9.75 14.6718 10.4216 14.0002 11.25 14.0002H15.25C16.0784 14.0002 16.75 14.6718 16.75 15.5002C16.75 16.3286 16.0784 17.0002 15.25 17.0002H11.25C10.4216 17.0002 9.75 16.3286 9.75 15.5002ZM11.25 9.00018C10.4216 9.00018 9.75 9.67176 9.75 10.5002C9.75 11.3286 10.4216 12.0002 11.25 12.0002H20.25C21.0784 12.0002 21.75 11.3286 21.75 10.5002C21.75 9.67176 21.0784 9.00018 20.25 9.00018H11.25Z"
      fill="#20272B"
      opacity={focused ? 1 : 0.3}
    />
  </SvgIcon>
);

const MyPageIcon = ({ focused }: { focused: boolean }) => (
  <SvgIcon
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="15"
      cy="9.5"
      r="4.5"
      fill="#20272B"
      opacity={focused ? 1 : 0.3}
    />
    <path
      d="M6 22.75C6 22.0535 6 21.7052 6.02889 21.4119C6.30946 18.5632 8.56324 16.3095 11.4119 16.0289C11.7052 16 12.0535 16 12.75 16H17.25C17.9465 16 18.2948 16 18.5881 16.0289C21.4368 16.3095 23.6905 18.5632 23.9711 21.4119C24 21.7052 24 22.0535 24 22.75C24 22.9822 24 23.0983 23.9904 23.196C23.8968 24.1456 23.1456 24.8968 22.196 24.9904C22.0983 25 21.9822 25 21.75 25H8.25C8.01783 25 7.90175 25 7.80397 24.9904C6.85441 24.8968 6.10315 24.1456 6.00963 23.196C6 23.0983 6 22.9822 6 22.75Z"
      fill="#20272B"
      opacity={focused ? 1 : 0.3}
    />
  </SvgIcon>
);

export default function PCSideTab() {
  const navigate = useNavigate();
  const location = useLocation();

  const userWebChatState = useRecoilValue(userWebChatAtom);
  const [pcSideTab, setPCSideTab] = useRecoilState(pcSideTabAtom);
  const previousTab = useRef<PCSideTabType>(pcSideTab);

  // 활성 탭 하이라이트는 atom 이 아니라 현재 URL 에서 파생한다 — atom 은 탭 클릭 시
  // 네비게이션 트리거로만 쓰고, 표시는 URL 기준으로 단일화해 "탭≠콘텐츠" 불일치를 막는다.
  const activeTab = pcTabFromPath(location.pathname);

  useEffect(() => {
    if (pcSideTab === "chat") {
      navigate("/chat", { replace: true });
    } else if (
      (previousTab.current === "chat" || previousTab.current === "mypage") &&
      pcSideTab === "home"
    ) {
      navigate("/progress", { replace: true });
    } else if (pcSideTab === "mypage") {
      // 이미 '나' 섹션 페이지(설정 등)에 안착해 있으면 /mypage 로 덮지 않는다.
      // (풀페이지 복귀로 /notification-settings 등에 직접 진입한 경우 탭=나 인 채 유지)
      if (!isMyPageSectionPath(window.location.pathname)) {
        navigate("/mypage", { replace: true });
      }
    }
    previousTab.current = pcSideTab;
  }, [pcSideTab]);

  return (
    <Stack
      sx={{
        width: "60px",
        height: "100%",
        backgroundColor: "background.level2",
        borderRight: "1px solid",
        borderColor: "divider",
        py: 2,
        alignItems: "center",
      }}
      spacing={"24px"}
    >
      <Box
        sx={{
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
          borderRadius: "8px",
        }}
        onClick={() => {
          setPCSideTab("home");
          navigate("/progress", { replace: true });
        }}
      >
        <HomeIcon focused={activeTab === "home"} />
        <Typography
          variant={"Footnote"}
          sx={{
            fontWeight: 600,
            color: theme.vars.palette.Content.Strong,
            opacity: activeTab === "home" ? 1 : 0.3,
          }}
        >
          홈
        </Typography>
      </Box>

      <Box
        sx={{
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
          borderRadius: "8px",
          position: "relative",
        }}
        onClick={() => {
          setPCSideTab("chat");
          navigate("/chat", { replace: true });
        }}
      >
        <ChatIcon focused={activeTab === "chat"} />
        <Typography
          variant={"Footnote"}
          sx={{
            fontWeight: 600,
            color: theme.vars.palette.Content.Strong,
            opacity: activeTab === "chat" ? 1 : 0.3,
          }}
        >
          코칭
        </Typography>

        {/* 안 읽은 메시지 배지 */}
        {(userWebChatState?.unreadMessageCount || 0) > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "-4px",
              right: "15px",
              minWidth: "16px",
              height: "16px",
              borderRadius: "8px",
              backgroundColor: "primary.solid",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: "4px",
            }}
          >
            {(userWebChatState?.unreadMessageCount || 0) > 99
              ? "99+"
              : userWebChatState?.unreadMessageCount}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
          borderRadius: "8px",
        }}
        onClick={() => {
          setPCSideTab("mypage");
          navigate("/mypage", { replace: true });
        }}
      >
        <MyPageIcon focused={activeTab === "mypage"} />
        <Typography
          variant={"Footnote"}
          sx={{
            fontWeight: 600,
            color: theme.vars.palette.Content.Strong,
            opacity: activeTab === "mypage" ? 1 : 0.3,
          }}
        >
          나
        </Typography>
      </Box>
    </Stack>
  );
}
